import React, { memo, useEffect, useRef, useState } from 'react';
import TeethPosition from '../../TeethPosition';
import { bottomLeftTeeth, bottomRightTeeth, TopTeeth } from './teethArchType';
import { Button, FormLabel } from '@material-ui/core';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { SelectRhf } from '../SelectRhf/SelectRhf';
import { useLang } from '../../../util/lang';
import { useSelector } from 'react-redux';

function arraysAreEqual(arr1, arr2) {
    // If the lengths are different, the arrays are not the same
    if (arr1.length !== arr2.length) {
        return false;
    }

    // Sort both arrays
    const sortedArr1 = arr1.slice().sort((a, b) => a - b);
    const sortedArr2 = arr2.slice().sort((a, b) => a - b);

    // Compare each element
    for (let i = 0; i < sortedArr1.length; i++) {
        if (sortedArr1[i] !== sortedArr2[i]) {
            return false;
        }
    }

    // If all elements are the same, return true
    return true;
}

// Synchronize quick dropdown options with manual tooth selection.
const MyTeethChoice = ({
    teethSelectionDisabled = false,
    question,
    name,
    optional = false,
    alwaysShowRequired = false,
    checkSuccess,
    quickTeethOption,
    imageScale = 1,
}) => {
    const [selectedTooth, setSelectedTooth] = useState([]);
    const {
        formState: { errors, isSubmitting, isValid, defaultValues },
        setValue,
        setError,
        clearErrors,
        getValues,
    } = useFormContext();
    const [shortCutTeeth, setShortCutTeeth] = useState([]);
    const selectedQuickOptionRef = useRef('');
    const get = useLang();

    const myTeethChoice = useForm({
        defaultValues: {
            quickTeethOption: '',
        },
    });
    const myTeethChoiceFormValue = myTeethChoice.watch();
    const displayFirstFitFlag = useSelector((state) => state.globalReducer.displayFirstFitFlag);

    // Reset the tooth selection and quick dropdown state.
    const handleResetValue = () => {
        setSelectedTooth([]);
        setValue(name, []);
        setShortCutTeeth([]);
        selectedQuickOptionRef.current = '';
        myTeethChoice.resetField('quickTeethOption');
    };

    // Preserve manual tooth selection while marking the quick dropdown as custom.
    const handleManualToothSelection = (teeth) => {
        selectedQuickOptionRef.current = 'custom';
        setShortCutTeeth(teeth);
        myTeethChoice.setValue('quickTeethOption', 'custom');
        setSelectedTooth(teeth);
    };

    useEffect(() => {
        setValue(name, selectedTooth);
        if (selectedTooth.length !== 0) {
            clearErrors(name);
        }

        if (!arraysAreEqual(selectedTooth, shortCutTeeth)) {
            myTeethChoice.reset();
        }
    }, [selectedTooth]);

    useEffect(() => {
        if (!optional && ((isSubmitting && selectedTooth.length === 0) || (!isValid && selectedTooth.length === 0))) {
            setError(name, {
                type: 'required',
                message: 'Required',
            });
        }
    }, [isSubmitting, isValid, selectedTooth.length]);

    useEffect(() => {
        if (getValues(name)) {
            setSelectedTooth(getValues(name)); //Explain: set default value
        }
    }, [defaultValues]);

    useEffect(() => {
        if (quickTeethOption?.length > 0 && !teethSelectionDisabled) {
            const selectedOption = quickTeethOption.find(
                (option) => option.value === myTeethChoiceFormValue.quickTeethOption
            );
            if (selectedOption) {
                if (selectedOption.value === 'custom' && selectedQuickOptionRef.current === 'custom') {
                    return;
                }
                selectedQuickOptionRef.current = selectedOption.value;
                const selectedTeeth = selectedOption.teeth || [];
                setSelectedTooth([]);
                setValue(name, []);
                setShortCutTeeth(selectedTeeth);
                setValue(name, selectedTeeth);
                setSelectedTooth(selectedTeeth);
            }
        }
    }, [myTeethChoiceFormValue.quickTeethOption]);

    return (
        <div style={{ marginBottom: 8 }}>
            <FormLabel
                component="legend"
                style={{ display: 'flex', alignItems: 'center', marginBottom: name === 'teethNotChange' ? 10 : 4 }}
            >
                {/* , fontWeight: name === 'teethNotChange' ? 'bold' : 'normal'  */}
                <span style={{ marginRight: 8}}>
                    {question}
                </span>
                {alwaysShowRequired ? (
                    <span style={{ color: 'red' }}>*</span>
                ) : (
                    <ErrorMessage
                        errors={errors}
                        name={name}
                        render={({ message }) => <span style={{ color: 'red' }}>*</span>}
                    />
                )}
            </FormLabel>
            <div style={{ overflowX: 'scroll', width: '100%' }}>
                <>
                    {quickTeethOption?.length > 0 && !teethSelectionDisabled && (
                        <div style={{ display: 'flex', alignItems: 'center' , marginLeft: 14}}>
                            <FormProvider {...myTeethChoice}>
                                <SelectRhf
                                    option={quickTeethOption.map((value) => {
                                        return {
                                            ...value,
                                            value: value.value,
                                            label: value.label.startsWith('@') ? get(value.label) : value.label,
                                        };
                                    })}
                                    name={'quickTeethOption'}
                                    label={'Shortcut'}
                                ></SelectRhf>
                            </FormProvider>
                            {
                                displayFirstFitFlag ? 
                                <Button
                                    variant="outlined"
                                    onClick={handleResetValue}
                                    style={{
                                        marginLeft: 8,
                                        minWidth: 72,
                                        height: 49,
                                        borderRadius: 8,
                                        color: 'black',
                                        borderColor: '#e4e4e9',
                                        backgroundColor: '#fff',
                                    }}>
                                    {get('@global.const.reset')}
                                </Button>
                                : <Button onClick={handleResetValue} style={{boxShadow:'0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',background:"#e0e0e0"}}>{get('@global.const.reset')}</Button>
                            }
                        </div>
                    )}
                    <TeethPosition
                        teeth={TopTeeth}
                        side={'Top'}
                        scale={imageScale}
                        disabled={teethSelectionDisabled}
                        selectedTooth={selectedTooth}
                        setSelectedTooth={handleManualToothSelection}
                        checkSuccess={checkSuccess}
                    />
                    <TeethPosition
                        side={''}
                        teeth={{
                            bottomRigth: {
                                ...bottomRightTeeth,
                                minY: 45,
                                maxY: 120,
                            },
                            bottomLeft: {
                                ...bottomLeftTeeth,
                                minY: 45,
                                maxY: 120,
                            },
                        }}
                        height={150}
                        scale={imageScale}
                        checkSuccess={checkSuccess}
                        disabled={teethSelectionDisabled}
                        selectedTooth={selectedTooth}
                        setSelectedTooth={handleManualToothSelection}
                    />
                </>
            </div>
        </div>
    );
};

export default memo(MyTeethChoice);
